<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  // AIVIO: optional count badge (used by the account-wide "Неотвеченные" item).
  count: { type: Number, default: 0 },
  // AIVIO unread: unread-conversations pill (upstream f33e469e9). One badge per
  // row: the AIVIO `count` badge wins when set, otherwise the unread pill.
  badgeCount: { type: [Number, String], default: 0 },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});

// AIVIO: cap display at 99+ like the group-header count badge.
const displayCount = computed(() => (props.count > 99 ? '99+' : props.count));
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ltr:pl-2 rtl:pr-2 rtl:mr-3 ltr:ml-3 relative text-n-slate-11 child-item before:bg-n-slate-4 after:bg-transparent after:border-n-slate-4 before:left-0 rtl:before:right-0 min-w-0"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="flex h-8 items-center gap-2 px-2 py-1 rounded-lg hover:bg-gradient-to-r from-transparent via-n-slate-3/70 to-n-slate-3/70 group min-w-0"
      :class="{
        'text-n-slate-12 bg-n-alpha-2 active': active,
      }"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        v-bind="{ label, icon, active, badgeCount }"
      />
      <template v-else>
        <span v-if="icon" class="size-4 grid place-content-center rounded-full">
          <Icon :icon="icon" class="size-4 inline-block" />
        </span>
        <div class="flex-1 truncate min-w-0 text-sm">{{ label }}</div>
        <!-- AIVIO: account-wide count badge (mirrors SidebarGroupHeader). -->
        <span
          v-if="count"
          v-tooltip.top="$t('SIDEBAR.AWAITING_REPLY_BADGE_TOOLTIP')"
          class="rounded-md text-xs leading-5 font-medium text-center outline outline-1 px-1 flex-shrink-0"
          :class="{
            'text-n-slate-12 outline-n-slate-6': active,
            'text-n-slate-11 outline-n-strong': !active,
          }"
        >
          {{ displayCount }}
        </span>
        <SidebarUnreadBadge v-else :count="badgeCount" />
      </template>
    </component>
  </Policy>
</template>
